import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';

const MiniExtendedHoursTable = ({ type, marketSession, title, linkTo, data }) => {
    const theme = useTheme();

    // Format price with 2 decimal places or more if needed
    const formatPrice = (price) => {
        if (price === undefined || price === null) return 'N/A';

        const priceValue = parseFloat(price);
        if (isNaN(priceValue)) return 'N/A';

        // For prices that need more precision (less than 0.01)
        if (Math.abs(priceValue) < 0.01 && priceValue !== 0) {
            return priceValue.toFixed(4);
        }

        return priceValue.toFixed(2);
    };

    // Show only top 10
    const displayData = data.slice(0, 10);

    return (
        <Box className="mini-table-container" mb={4}>
            <Box mb={2}>
                <RouterLink
                    to={linkTo}
                    style={{
                        color: theme.palette.text.primary,
                        textDecoration: 'none',
                        display: 'inline-block'
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            '&:hover': {
                                color: theme.palette.primary.main,
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {title}
                    </Typography>
                </RouterLink>
            </Box>

            {displayData.length === 0 ? (
                <Typography variant="body2" align="center" p={2} color="text.secondary">
                    No data available
                </Typography>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: 1,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Table
                        size="small"
                        sx={{
                            minWidth: 320,
                            '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                lineHeight: '1.3',
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                borderBottom: `2px solid ${theme.palette.divider}`,
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        padding: { xs: '6px 8px', sm: '8px 12px' }
                                    }}
                                >
                                    Company
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        padding: { xs: '8px 4px', sm: '12px 16px' },
                                    }}
                                >
                                    {marketSession === 'pre' ? 'Pre-Market' : 'After-Market'}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        padding: { xs: '8px 4px', sm: '12px 16px' },
                                    }}
                                >
                                    Close
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        padding: { xs: '8px 4px', sm: '12px 16px' },
                                    }}
                                >
                                    Change
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayData.map((row) => (
                                <TableRow
                                    key={row.symbol}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        sx={{
                                            padding: { xs: '6px 8px', sm: '8px 12px' }
                                        }}
                                    >
                                        <RouterLink
                                            to={`/asset/${row.symbol}`}
                                            style={{
                                                color: theme.palette.primary.main,
                                                textDecoration: 'none',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            {row.symbol}
                                        </RouterLink>
                                        <div style={{
                                            fontSize: '0.85em',
                                            color: '#888',
                                            marginTop: '1px',
                                            lineHeight: '1.2'
                                        }}>
                                            {row.name}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            padding: { xs: '8px 4px', sm: '12px 16px' },
                                        }}
                                    >
                                        {formatPrice(row.price)}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            padding: { xs: '8px 4px', sm: '12px 16px' },
                                        }}
                                    >
                                        {formatPrice(row.regularPrice)}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            padding: { xs: '8px 4px', sm: '12px 16px' },
                                            color: row.percentChange > 0
                                                ? theme.palette.positive.main
                                                : row.percentChange < 0
                                                    ? theme.palette.negative.main
                                                    : theme.palette.lightGray.main
                                        }}
                                    >
                                        {formatPrice(row.percentChange)}%
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default MiniExtendedHoursTable;